import { UpdateDeviceCharacteristicGroupCharacteristicsRequestBody } from 'features/eai/eaiTypes';
import {
  CharacteristicsRk,
  getBooleanValueOrDefaultValue,
  getNumberValueOrDefaultValue,
  getStringValueOrDefaultValue,
  PoweroutputChangeType,
  UnitType
} from 'utils/characteristic';
import { ValidationPatterns, YupType } from 'utils/constants';
import {
  CharacteristicSpecificationInterface,
  CharacteristicValueInterface
} from 'models/characteristics';
import { FormInput } from 'models/form';

// ==============================|| UTILS ||============================== //

export type ReservedCapacityGuardingParametersState = {
  reserved_capacity_guard_enabled: boolean;
  reserved_capacity_guard_priority: string;
  reserved_capacity_max_restriction_duration: string;
  reserved_capacity_max_reduction_limit: string;
  reserved_capacity_max_reduction_limit_type: string;
  reserved_capacity_max_change_of_rate_of_power_change_reduction: string;
};

export const getReservedCapacityGuardingParametersInitialState = (
  characteristics: CharacteristicValueInterface[],
  characteristicSpecifications: CharacteristicSpecificationInterface[]
): ReservedCapacityGuardingParametersState => {
  return {
    reserved_capacity_guard_enabled:
      getBooleanValueOrDefaultValue(
        characteristics,
        characteristicSpecifications,
        CharacteristicsRk.reserved_capacity_guard_enabled
      ) ?? false,
    reserved_capacity_guard_priority:
      getNumberValueOrDefaultValue(
        characteristics,
        characteristicSpecifications,
        CharacteristicsRk.reserved_capacity_guard_priority
      ) ?? '',
    reserved_capacity_max_restriction_duration:
      getNumberValueOrDefaultValue(
        characteristics,
        characteristicSpecifications,
        CharacteristicsRk.reserved_capacity_max_restriction_duration
      ) ?? '',
    reserved_capacity_max_reduction_limit:
      getNumberValueOrDefaultValue(
        characteristics,
        characteristicSpecifications,
        CharacteristicsRk.reserved_capacity_max_reduction_limit
      ) ?? '',
    reserved_capacity_max_reduction_limit_type:
      getStringValueOrDefaultValue(
        characteristics,
        characteristicSpecifications,
        CharacteristicsRk.reserved_capacity_max_reduction_limit_type
      ) ?? '',
    reserved_capacity_max_change_of_rate_of_power_change_reduction:
      getNumberValueOrDefaultValue(
        characteristics,
        characteristicSpecifications,
        CharacteristicsRk.reserved_capacity_max_change_of_rate_of_power_change_reduction
      ) ?? ''
  };
};

export enum DeviceReservedCapacityGuardingCharacteristicsValidationArguments {
  RESERVED_CAPACITY_GUARD_PRIORITY_REQUIRED = 'reserved_capacity_guard_priority-required',
  RESERVED_CAPACITY_GUARD_PRIORITY_NUMBER = 'reserved_capacity_guard_priority-number',
  RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_REQUIRED = 'reserved_capacity_max_restriction_duration-required',
  RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_NUMBER = 'reserved_capacity_max_restriction_duration-number',
  RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_REQUIRED = 'reserved_capacity_max_reduction_limit-required',
  RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_NUMBER = 'reserved_capacity_max_reduction_limit-number',
  RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_TYPE_REQUIRED = 'reserved_capacity_max_reduction_limit_type-required',
  RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_REQUIRED = 'reserved_capacity_max_change_of_rate_of_power_change_reduction-required',
  RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_NUMBER = 'reserved_capacity_max_change_of_rate_of_power_change_reduction-number'
}

export const createDeviceReservedCapacityGuardingCharacteristicsFormInputs = (
  powerOutputChangeType?: string
): FormInput[] => {
  return [
    {
      id: CharacteristicsRk.reserved_capacity_guard_enabled,
      validation: {
        type: YupType.BOOLEAN
      }
    },
    {
      id: CharacteristicsRk.reserved_capacity_guard_priority,
      validation: {
        type: YupType.STRING,
        methods: [
          {
            methodType: 'trim'
          },
          {
            methodType: 'required',
            args: [
              DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_REQUIRED
            ]
          },
          {
            methodType: 'matches',
            args: [
              ValidationPatterns.POSITIVE_INTEGER,
              DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_NUMBER
            ]
          }
        ]
      }
    },
    {
      id: CharacteristicsRk.reserved_capacity_max_restriction_duration,
      validation: {
        type: YupType.STRING,
        methods: [
          {
            methodType: 'trim'
          },
          {
            methodType: 'required',
            args: [
              DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_REQUIRED
            ]
          },
          {
            methodType: 'matches',
            args: [
              ValidationPatterns.POSITIVE_INTEGER,
              DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_NUMBER
            ]
          }
        ]
      }
    },
    {
      id: CharacteristicsRk.reserved_capacity_max_reduction_limit,
      validation: {
        type: YupType.STRING,
        methods: [
          {
            methodType: 'trim'
          },
          powerOutputChangeType ===
          PoweroutputChangeType.ENTIRE_AVAILABLE_CONSUMPTION_POWER_SWITCH
            ? {
                methodType: 'required',
                args: [
                  DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_REQUIRED
                ]
              }
            : { methodType: 'nullable' },
          {
            methodType: 'matches',
            args: [
              ValidationPatterns.POSITIVE_INTEGER,
              DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_NUMBER
            ]
          }
        ]
      }
    },
    {
      id: CharacteristicsRk.reserved_capacity_max_reduction_limit_type,
      validation: {
        type: YupType.STRING,
        methods: [
          {
            methodType: 'trim'
          },
          powerOutputChangeType ===
          PoweroutputChangeType.LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_FIXED
            ? {
                methodType: 'required',
                args: [
                  DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_TYPE_REQUIRED
                ]
              }
            : { methodType: 'nullable' }
        ]
      }
    },
    {
      id: CharacteristicsRk.reserved_capacity_max_change_of_rate_of_power_change_reduction,
      validation: {
        type: YupType.STRING,
        methods: [
          {
            methodType: 'trim'
          },
          powerOutputChangeType ===
          PoweroutputChangeType.LINEAR_CONTINUOUS_CONSUMPTION_POWER_CHANGE_ADJUSTABLE
            ? {
                methodType: 'required',
                args: [
                  DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_REQUIRED
                ]
              }
            : { methodType: 'nullable' },
          {
            methodType: 'matches',
            args: [
              ValidationPatterns.POSITIVE_NUMBER,
              DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_NUMBER
            ]
          }
        ]
      }
    }
  ];
};

export const getUpdateDeviceCharacteristicGroupRequestBody = ({
  reserved_capacity_guard_enabled,
  reserved_capacity_guard_priority,
  reserved_capacity_max_reduction_limit,
  reserved_capacity_max_restriction_duration,
  reserved_capacity_max_reduction_limit_type,
  reserved_capacity_max_change_of_rate_of_power_change_reduction
}: ReservedCapacityGuardingParametersState): UpdateDeviceCharacteristicGroupCharacteristicsRequestBody => {
  return {
    characteristics: [
      {
        name: CharacteristicsRk.reserved_capacity_guard_enabled,
        booleanValue: reserved_capacity_guard_enabled,
        valueType: 'BOOLEAN',
        unitOfMeasure: undefined
      },
      {
        name: CharacteristicsRk.reserved_capacity_guard_priority,
        numberValue: Number(reserved_capacity_guard_priority),
        valueType: 'NUMBER',
        unitOfMeasure: undefined
      },
      {
        name: CharacteristicsRk.reserved_capacity_max_reduction_limit,
        numberValue: Number(reserved_capacity_max_reduction_limit),
        valueType: 'NUMBER',
        unitOfMeasure: UnitType.KW
      },
      {
        name: CharacteristicsRk.reserved_capacity_max_restriction_duration,
        numberValue: reserved_capacity_max_restriction_duration
          ? Number(reserved_capacity_max_restriction_duration)
          : null,
        valueType: 'NUMBER',
        unitOfMeasure: UnitType.SECOND
      },
      {
        name: CharacteristicsRk.reserved_capacity_max_reduction_limit_type,
        stringValue: reserved_capacity_max_reduction_limit_type || null,
        valueType: 'STRING',
        unitOfMeasure: undefined
      },
      {
        name: CharacteristicsRk.reserved_capacity_max_change_of_rate_of_power_change_reduction,
        numberValue:
          reserved_capacity_max_change_of_rate_of_power_change_reduction
            ? Number(
                reserved_capacity_max_change_of_rate_of_power_change_reduction
              )
            : null,
        valueType: 'NUMBER',
        unitOfMeasure: UnitType.KILOWATT_PER_SECOND
      }
    ]
  };
};
